import { useEffect, useMemo, useRef, useState } from 'react';
import { Palette, Check } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../auth/AuthProvider';
import { THEMES, type ThemeMeta } from '../lib/themes';

const GROUP_LABEL: Record<NonNullable<ThemeMeta['group']>, string> = {
  vibe: 'Vibe',
  brand: 'Brand',
  utility: 'Utility',
};

// Header-level theme picker. A 36x36 button that opens a popup with every
// theme rendered as a swatch. One-click apply, persists via useTheme.
//
// Disabled in preview mode — admins viewing-as someone else shouldn't be
// nudging that user's theme by accident.

export const ThemeMenu = () => {
  const { theme, setTheme, syncing } = useTheme();
  const { previewUserId } = useAuth();
  const readOnly = previewUserId !== null;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Group themes for the menu — vibe first, then brand, then utility.
  const grouped = useMemo(() => {
    const order: NonNullable<ThemeMeta['group']>[] = ['vibe', 'brand', 'utility'];
    return order.map((g) => ({
      group: g,
      label: GROUP_LABEL[g],
      themes: THEMES.filter((t) => t.group === g),
    }));
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={readOnly}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Theme"
        title={readOnly ? 'Theme disabled in preview mode' : 'Change theme'}
        className="grid h-11 w-11 place-items-center rounded-md border border-line bg-bg-secondary text-text-secondary transition-colors hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Palette className="h-4 w-4" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Themes"
          className="absolute right-0 z-40 mt-1.5 w-72 overflow-hidden rounded-lg border border-line bg-bg-primary shadow-lg"
        >
          <div className="border-b border-line px-3 py-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
              Theme
            </div>
            <div className="mt-0.5 text-[11px] text-text-tertiary">
              Saved instantly · syncs across devices.
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto py-1">
            {grouped.map(({ group, label, themes: groupThemes }) => (
              <div key={group}>
                <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-text-tertiary">
                  {label}
                </div>
                <ul>
                  {groupThemes.map((t) => {
                    const active = t.id === theme;
                    return (
                      <li key={t.id}>
                        <button
                          type="button"
                          role="menuitemradio"
                          aria-checked={active}
                          disabled={syncing && active}
                          onClick={() => {
                            setTheme(t.id);
                            setTimeout(() => setOpen(false), 80);
                          }}
                          className={[
                            'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors',
                            active
                              ? 'bg-brand-navy/[0.06] text-text-primary'
                              : 'text-text-secondary hover:bg-bg-secondary hover:text-text-primary',
                          ].join(' ')}
                        >
                          {/* Mini swatch */}
                          <span
                            aria-hidden="true"
                            className="grid h-8 w-8 shrink-0 grid-cols-2 grid-rows-2 overflow-hidden rounded-md border border-line"
                          >
                            <span style={{ background: t.preview.bg }} />
                            <span style={{ background: t.preview.surface }} />
                            <span style={{ background: t.preview.accent }} />
                            <span style={{ background: t.preview.text }} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-medium">{t.label}</span>
                            <span className="block truncate text-[11px] text-text-tertiary">
                              {t.description}
                            </span>
                          </span>
                          {active && (
                            <Check className="h-4 w-4 shrink-0 text-brand-orange" aria-hidden="true" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
